import { useCallback, useEffect, useRef, useState } from "react";

import type { SelectOptionBase } from "src/components/Select/types";

interface UseAsyncOptionsConfig {
  loadOptions?: (inputValue: string) => Promise<SelectOptionBase[]>;
  defaultOptions?: boolean | SelectOptionBase[];
  cacheOptions?: boolean;
}

interface UseAsyncOptionsReturn {
  asyncOptions: SelectOptionBase[];
  isLoading: boolean;
  handleAsyncInputChange: (value: string) => void;
}

export function useAsyncOptions({
  loadOptions,
  defaultOptions = false,
  cacheOptions = false,
}: UseAsyncOptionsConfig): UseAsyncOptionsReturn {
  const [asyncOptions, setAsyncOptions] = useState<SelectOptionBase[]>(() =>
    Array.isArray(defaultOptions) ? defaultOptions : []
  );
  const [isLoading, setIsLoading] = useState(false);
  const cacheRef = useRef<Record<string, SelectOptionBase[]>>({});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRequestRef = useRef(0);
  const mountedRef = useRef(true);

  // Load default options on mount when `defaultOptions === true`
  useEffect(() => {
    if (!loadOptions || defaultOptions !== true) return;
    let cancelled = false;
    setIsLoading(true);
    loadOptions("")
      .then(results => {
        if (cancelled) return;
        setAsyncOptions(results);
        if (cacheOptions) cacheRef.current[""] = results;
        setIsLoading(false);
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Clean up debounce timer and mark unmounted
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleAsyncInputChange = useCallback(
    (value: string) => {
      if (!loadOptions) return;

      const query = value.trim();

      // Check cache first
      if (cacheOptions && query in cacheRef.current) {
        setAsyncOptions(cacheRef.current[query]);
        return;
      }

      // Debounce the fetch
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const requestId = ++latestRequestRef.current;
        setIsLoading(true);
        loadOptions(query)
          .then(results => {
            if (requestId !== latestRequestRef.current || !mountedRef.current)
              return;
            setAsyncOptions(results);
            if (cacheOptions) cacheRef.current[query] = results;
            setIsLoading(false);
          })
          .catch(() => {
            if (requestId === latestRequestRef.current && mountedRef.current) {
              setIsLoading(false);
            }
          });
      }, 300);
    },
    [loadOptions, cacheOptions]
  );

  return { asyncOptions, isLoading, handleAsyncInputChange };
}
